import { ApiRetailerPaymentType } from '@vend/types'
import { useCallback } from 'react'
import { vn } from 'vend-number'
import { ToastNotifications } from '@lightspeed/design-system-react'
import { t } from '@transifex/native'
import { useAppSelector } from '../../../../state/hooks'
import { PartialPaymentTypeName } from '../../../payments/partial-payment-modal/PartialPaymentModal'
import { selectOutstandingBalance } from '../../../../state/slices/currentSale/currentSaleSlice'
import { PaymentModalType, SalePayment } from '../PaymentModal/types'
import { useAvailableStoreCredit } from './useAvailableStoreCredit'
import { usePaymentAmountForCashDiscounting } from './useGetPaymentAmountForCashDiscounting'

const STORE_CREDIT_PAYMENT_OVER_BALANCE = `Amount tendered exceeds payment balance.
    Please change the amount to pay with Store Credit, or choose another payment type.`

const STORE_CREDIT_INVALID_TENDERED_AMOUNT_ENTERED = `Invalid tendered amount.
    Please change the amount to tender, or choose another payment type.`

const overBalanceMessage = t(STORE_CREDIT_PAYMENT_OVER_BALANCE, {
  _context: 'Store Credit payment invalid',
})

const invalidTenderedAmountMessage = t(
  STORE_CREDIT_INVALID_TENDERED_AMOUNT_ENTERED,
  { _context: 'Store Credit payment invalid' }
)

/**
 * Hook to handle payment via store credit
 */
export const useHandleStoreCreditPayment = (
  amountTendered: string,
  handleCompletePayment: (payment: SalePayment) => Promise<void>
) => {
  const oustandingBalance = useAppSelector(selectOutstandingBalance)
  const {
    getPaymentAmountForCashDiscounting,
  } = usePaymentAmountForCashDiscounting()

  const availableStoreCredit = useAvailableStoreCredit()

  const handleStoreCreditPayment = useCallback(
    (paymentType: ApiRetailerPaymentType) => {
      const amountToPay = getPaymentAmountForCashDiscounting(amountTendered)
      const hasPositiveBalance = !vn(oustandingBalance).isNeg()
      const vnAmountToPay = vn(amountToPay)

      if (hasPositiveBalance) {
        // Do not allow the user to pay with more store credit than the sale balance
        // This avoids the customer cashing out store credit
        if (vnAmountToPay.gt(oustandingBalance)) {
          ToastNotifications.negative(overBalanceMessage)
          return
        }
        // When we have a positive balance we also do not allow negative tendered amounts with store credits
        if (vnAmountToPay.isNeg()) {
          ToastNotifications.negative(invalidTenderedAmountMessage)
          return
        }

        // If tendered amount is > available store credit, allow customer to make a partial payment
        if (vnAmountToPay.gt(availableStoreCredit)) {
          const paymentTypeName: PartialPaymentTypeName = 'Store Credit'
          const paymentModalType: PaymentModalType = 'partialPayment'
          return {
            paymentTypeName,
            availableCredit: availableStoreCredit,
            paymentType,
            paymentModalType,
          }
        }

        // Complete payment if none of the above conditions are met
        handleCompletePayment({
          paymentAmount: amountToPay!,
          paymentType,
        })
        return
      } else if (!hasPositiveBalance && !vn(amountTendered).isNeg()) {
        // return sale but amount tendered positive display notification
        ToastNotifications.negative(invalidTenderedAmountMessage)
        return
      } else {
        // return sale and amount tender negative, proceed payment
        handleCompletePayment({
          paymentAmount: amountToPay!,
          paymentType,
        })
        return
      }
    },
    [
      getPaymentAmountForCashDiscounting,
      amountTendered,
      oustandingBalance,
      availableStoreCredit,
      handleCompletePayment,
    ]
  )
  return {
    handleStoreCreditPayment,
  }
}
